import React, { useEffect, useState } from 'react';
import { useColorTheme } from '../../context/ColorThemeContext';

const SubscriptionSuccess = ({ sessionToken, onNavigate }) => {
  const { currentColors, colorTheme } = useColorTheme();
  const [status, setStatus] = useState('verifying'); // verifying, success, error

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId) {
      // Simulate verification (webhook will handle the actual subscription activation)
      setTimeout(() => {
        setStatus('success');
        // Clear URL params
        window.history.replaceState({}, document.title, '/subscription/success');
      }, 2000);
    } else {
      setStatus('error');
    }
  }, []);

  const handleGoToCredits = () => {
    if (onNavigate) {
      onNavigate('credits');
    } else {
      window.location.href = '/credits';
    }
  };

  const handleGoToDashboard = () => {
    if (onNavigate) {
      onNavigate('dashboard');
    } else {
      window.location.href = '/dashboard';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {status === 'verifying' && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Activating Your Subscription
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Please wait while we confirm your payment...
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center relative overflow-hidden">
            {/* Confetti effect */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-4 left-4 text-2xl animate-bounce">🎉</div>
              <div className="absolute top-8 right-8 text-2xl animate-bounce" style={{ animationDelay: '0.2s' }}>🎊</div>
              <div className="absolute bottom-8 left-8 text-2xl animate-bounce" style={{ animationDelay: '0.4s' }}>✨</div>
              <div className="absolute bottom-4 right-4 text-2xl animate-bounce" style={{ animationDelay: '0.6s' }}>🌟</div>
            </div>

            <div className={`w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br ${
              colorTheme === 'purple' ? 'from-purple-400 to-purple-600' :
              colorTheme === 'blue' ? 'from-blue-400 to-blue-600' :
              'from-green-400 to-green-600'
            } flex items-center justify-center shadow-lg`}>
              <span className="text-4xl">✓</span>
            </div>

            <h2 className={`text-3xl font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r ${
              colorTheme === 'purple' ? 'from-purple-500 to-purple-700' :
              colorTheme === 'blue' ? 'from-blue-500 to-blue-700' :
              'from-green-500 to-green-700'
            }`}>
              Subscription Activated!
            </h2>

            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Your subscription is now active. Credits have been added to your account.
            </p>

            <div className={`bg-gradient-to-r ${
              colorTheme === 'purple' ? 'from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20' :
              colorTheme === 'blue' ? 'from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20' :
              'from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20'
            } rounded-xl p-4 mb-6`}>
              <div className="flex items-center justify-center gap-2 text-gray-700 dark:text-gray-300">
                <span>🔄</span>
                <span>Your credits will automatically renew each billing cycle</span>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleGoToCredits}
                className={`w-full py-3 px-4 rounded-xl font-semibold text-white transition-all transform hover:scale-105 shadow-lg ${
                  colorTheme === 'purple' 
                    ? 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700'
                    : colorTheme === 'blue'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
                    : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'
                }`}
              >
                View My Subscription
              </button>
              <button
                onClick={handleGoToDashboard}
                className="w-full py-3 px-4 rounded-xl font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
              <span className="text-4xl">❌</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Something Went Wrong
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              We couldn't verify your subscription. Please contact support if you were charged.
            </p>
            <button
              onClick={handleGoToCredits}
              className="w-full py-3 px-4 rounded-xl font-semibold text-white bg-gray-600 hover:bg-gray-700 transition-all"
            >
              Go Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
